/**
 * Pure helpers for console ClubsView (list, edit payload).
 */

import { PACE_RANGES } from "../constants/clubConstants";
import { CLUB_EDITABLE_LINK_FIELDS } from "./consoleUtils";
import { ONBOARD_OPTIONAL_FIELDS, INTERNAL_NOTES_MAX_LENGTH } from "./consoleOnboardPayload";

const EDIT_FORM_LINK_DEFAULTS = Object.fromEntries(Object.keys(CLUB_EDITABLE_LINK_FIELDS).map((k) => [k, ""]));

/** Meeting statuses considered visible in app (aligns with backend MEETING_STATUS_VISIBLE). */
export const MEETING_STATUS_VISIBLE = new Set(["published", "upcoming", "completed"]);

export function isMeetingVisible(run) {
  const s = run?.status;
  return typeof s === "string" && MEETING_STATUS_VISIBLE.has(s.toLowerCase());
}

/** Normalize pace label for comparison (en-dash vs hyphen). */
export function normalizePaceLabel(label) {
  if (label == null || typeof label !== "string") return "";
  return label.trim().replace(/\u2013/g, "-");
}

/** Map API pace labels to PACE_RANGES codes (handles en-dash vs hyphen). Same as mobile editClubUtils. */
export function paceLabelsToCodes(labels) {
  if (!Array.isArray(labels)) return [];
  return labels
    .map((label) => {
      const norm = normalizePaceLabel(label);
      const match = PACE_RANGES.find((r) => normalizePaceLabel(r.label) === norm);
      return match?.value;
    })
    .filter(Boolean);
}

/** Edit form shape: club fields only (no email/password). */
export const INITIAL_EDIT_FORM = {
  run_club_name: "",
  city: "",
  state: "",
  country: "",
  type: "",
  description: "",
  skill_level: "All levels",
  home_base_address: "",
  home_base_description: "",
  latitude: null,
  longitude: null,
  paces: [],
  club_notes: ["", "", ""],
  recurring_runs_vary: false,
  is_anchor_club: false,
  internal_notes: "",
  ...EDIT_FORM_LINK_DEFAULTS,
};

export function buildEditPayload(form, cityId) {
  const name = (form.run_club_name ?? "").trim();
  const city = (form.city ?? "").trim();
  const payload = { name, city };
  ONBOARD_OPTIONAL_FIELDS.forEach((key) => {
    const value = (form[key] ?? "").trim();
    if (value) payload[key] = value;
  });
  const website = (form.website ?? "").trim().slice(0, CLUB_EDITABLE_LINK_FIELDS.website.maxLen);
  if (website) payload.website = website;
  const paceCodes = Array.isArray(form.paces) ? form.paces.filter(Boolean).slice(0, 3) : [];
  if (paceCodes.length > 0) {
    payload.paces = paceCodes.map((code) => PACE_RANGES.find((r) => r.value === code)?.label ?? (typeof code === "string" ? code : String(code))).filter(Boolean);
  }
  const notes = Array.isArray(form.club_notes) ? form.club_notes.map((n) => (n ?? "").trim()).filter(Boolean).slice(0, 3) : [];
  if (notes.length > 0) payload.club_notes = notes;
  if (cityId) payload.city_id = cityId;
  payload.recurring_runs_vary = Boolean(form.recurring_runs_vary);
  payload.is_anchor_club = Boolean(form.is_anchor_club);
  const internalNotesEdit = (form.internal_notes ?? "").trim().slice(0, INTERNAL_NOTES_MAX_LENGTH);
  payload.internal_notes = internalNotesEdit || null;
  const lat = form.latitude != null ? Number(form.latitude) : null;
  const lon = form.longitude != null ? Number(form.longitude) : null;
  if (
    (form.home_base_address ?? "").trim() &&
    lat != null &&
    lon != null &&
    Number.isFinite(lat) &&
    Number.isFinite(lon) &&
    lat >= -90 &&
    lat <= 90 &&
    lon >= -180 &&
    lon <= 180
  ) {
    payload.latitude = lat;
    payload.longitude = lon;
  }
  return payload;
}

/** Human-readable label for Strava type values (e.g. "casual_club" → "Casual Club"). */
export function formatStravaTypeLabel(str) {
  if (!str || typeof str !== "string") return "";
  return str.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
